import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { apiClient } from '../services/http.js';
import { useToast } from '../hooks/useToast.jsx';
import { ErrorRetryCard } from '../components/common/ErrorRetryCard.jsx';

function SaveToNotebookForm({ enrollmentId, day, onSaved }) {
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const { showApiErrorToast, showToast } = useToast();

  const handleSave = async () => {
    const trimmed = content.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      const { res, data } = await apiClient.request(`/reading/enrollments/${enrollmentId}/days/${day}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: trimmed }),
      });
      if (res.ok) {
        showToast({ type: 'success', message: 'Reflexão salva no Caderno.' });
        setContent('');
        setOpen(false);
        onSaved?.(data);
      } else {
        showApiErrorToast(res, data, 'Não foi possível salvar no Caderno.');
      }
    } catch {
      showApiErrorToast(null, null, 'Não foi possível salvar no Caderno.');
    }
    setSaving(false);
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="px-4 py-2 bg-surface-container-high text-on-surface font-bold rounded hover:opacity-90 text-sm"
      >
        Salvar reflexão no Caderno
      </button>
    );
  }

  return (
    <div className="bg-surface-container-highest rounded-lg p-4 space-y-3">
      <label htmlFor="reflexao" className="text-xs font-medium text-on-surface-variant block">
        Sua reflexão sobre a leitura de hoje
      </label>
      <textarea
        id="reflexao"
        value={content}
        onChange={(e) => setContent(e.target.value)}
        rows={4}
        maxLength={8000}
        className="w-full rounded border border-outline-variant bg-surface p-2 text-sm"
        placeholder="O que este texto falou ao seu coração hoje?"
      />
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="px-3 py-1.5 text-sm rounded border border-outline-variant text-on-surface-variant hover:bg-surface-container-high"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !content.trim()}
          className="px-3 py-1.5 text-sm bg-primary text-on-primary font-bold rounded hover:opacity-90 disabled:opacity-50"
        >
          Salvar no Caderno
        </button>
      </div>
    </div>
  );
}

function DeepenSection({ enrollmentId, day, deepenCost, deepenAvailable }) {
  const [deepen, setDeepen] = useState(null);
  const [loading, setLoading] = useState(false);
  const { showApiErrorToast } = useToast();

  const fetchDeepen = async () => {
    setLoading(true);
    try {
      const { res, data } = await apiClient.request(`/reading/enrollments/${enrollmentId}/days/${day}/deepen`, { method: 'POST' });
      if (res.ok) {
        setDeepen(data);
      } else {
        showApiErrorToast(res, data, 'Não foi possível gerar o aprofundamento com IA.');
      }
    } catch {
      showApiErrorToast(null, null, 'Não foi possível gerar o aprofundamento com IA.');
    }
    setLoading(false);
  };

  // Se já existe conteúdo gerado antes, buscar é gratuito e idempotente
  // (POST .../deepen só cobra crédito na primeira geração) — carrega
  // automaticamente para não obrigar o usuário a clicar de novo após recarregar a página.
  useEffect(() => {
    setDeepen(null);
    if (deepenAvailable) fetchDeepen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrollmentId, day, deepenAvailable]);

  if (deepen) {
    return (
      <div className="bg-tertiary-container border border-outline-variant rounded-lg p-4 space-y-2">
        <p className="text-xs font-bold text-on-tertiary-container uppercase tracking-wide">
          Aprofundamento com IA
        </p>
        <p className="text-sm text-on-tertiary-container whitespace-pre-wrap">{deepen.content}</p>
        {deepen.citations && deepen.citations.length > 0 && (
          <ul className="text-xs text-on-tertiary-container/80 list-disc list-inside pt-1">
            {deepen.citations.map((c, idx) => (
              <li key={idx}>{c.title} ({c.doc_type})</li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={fetchDeepen}
      disabled={loading}
      className="px-4 py-2 bg-surface-container-high text-on-surface font-bold rounded hover:opacity-90 disabled:opacity-50 text-sm"
    >
      {loading ? 'Gerando...' : `Aprofundar com IA (${deepenCost} créditos)`}
    </button>
  );
}

export default function LeituraDiaPage() {
  const { enrollmentId, day } = useParams();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [contentError, setContentError] = useState(null);
  const [busy, setBusy] = useState(false);
  const { showApiErrorToast, showToast } = useToast();

  const fetchContent = async () => {
    setContentError(null);
    try {
      const { res, data } = await apiClient.request(`/reading/enrollments/${enrollmentId}/days/${day}`);
      if (res.ok) {
        setContent(data);
        return;
      }
      setContentError(apiClient.parseApiError(data, 'Não foi possível carregar a leitura de hoje.'));
    } catch {
      setContentError('Falha de conexão ao carregar a leitura de hoje.');
    }
  };

  useEffect(() => { setTimeout(() => { fetchContent(); setContent(null); }, 0); }, [enrollmentId, day]);

  const handleComplete = async () => {
    setBusy(true);
    try {
      const { res, data } = await apiClient.request(`/reading/enrollments/${enrollmentId}/days/${day}/complete`, { method: 'POST' });
      if (res.ok) {
        showToast({ type: 'success', message: 'Dia concluído. Continue quando quiser.' });
        navigate('/leitura');
      } else {
        showApiErrorToast(res, data, 'Não foi possível concluir este dia.', { onRetry: handleComplete });
      }
    } catch {
      showApiErrorToast(null, null, 'Não foi possível concluir este dia.', { onRetry: handleComplete });
    }
    setBusy(false);
  };

  if (content === null) {
    return (
      <div className="min-h-screen bg-surface text-on-surface p-6">
        <div className="max-w-2xl mx-auto space-y-4">
          <Link to="/leitura" className="inline-flex items-center gap-2 px-4 py-2 bg-surface-container-low hover:bg-surface-container-high rounded-lg font-medium transition-colors border border-outline-variant">
            <span>←</span> Voltar para Leitura
          </Link>
          {contentError ? (
            <ErrorRetryCard message={contentError} onRetry={fetchContent} />
          ) : (
            <p className="text-sm text-on-surface-variant">Carregando...</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface text-on-surface p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        <Link to="/leitura" className="inline-flex items-center gap-2 px-4 py-2 bg-surface-container-low hover:bg-surface-container-high rounded-lg font-medium transition-colors border border-outline-variant">
          <span>←</span> Voltar para Leitura
        </Link>

        <div>
          <p className="text-xs text-on-surface-variant uppercase tracking-wide font-bold">Dia {content.day_number}</p>
          <h1 className="text-2xl font-bold">{content.title}</h1>
        </div>

        {/* Texto bíblico: sempre em sua própria seção, nunca misturado com contexto/IA. */}
        <div className="space-y-4">
          {content.passages.map((passage, idx) => (
            <div key={idx} className="bg-surface-container-low border border-outline-variant rounded-lg p-4 space-y-2">
              <p className="text-xs font-bold text-primary">{passage.reference}</p>
              {passage.verses.map((v) => (
                <p key={v.verse} className="text-base leading-relaxed">
                  <sup className="text-xs text-on-surface-variant mr-1">{v.verse}</sup>
                  {v.text}
                </p>
              ))}
            </div>
          ))}
        </div>

        {content.context && (
          <div className="bg-surface-container-low border border-outline-variant rounded-lg p-4 space-y-1">
            <p className="text-xs font-bold text-on-surface-variant uppercase tracking-wide">Contexto</p>
            <p className="text-sm text-on-surface-variant whitespace-pre-wrap">{content.context}</p>
          </div>
        )}

        {content.questions && content.questions.length > 0 && (
          <div className="bg-surface-container-low border border-outline-variant rounded-lg p-4 space-y-2">
            <p className="text-xs font-bold text-on-surface-variant uppercase tracking-wide">Para refletir</p>
            <ul className="list-disc list-inside text-sm space-y-1">
              {content.questions.map((q, idx) => (
                <li key={idx}>{q}</li>
              ))}
            </ul>
          </div>
        )}

        <DeepenSection
          enrollmentId={enrollmentId}
          day={day}
          deepenCost={content.deepen_cost}
          deepenAvailable={content.deepen_available}
        />

        <div className="flex flex-wrap items-center justify-between gap-3">
          <SaveToNotebookForm enrollmentId={enrollmentId} day={day} />
          {content.completed ? (
            <span className="px-4 py-2 bg-primary-container text-on-primary-container font-bold rounded text-sm">
              Dia já concluído
            </span>
          ) : (
            <button
              type="button"
              onClick={handleComplete}
              disabled={busy}
              className="px-4 py-2 bg-primary text-on-primary font-bold rounded hover:opacity-90 disabled:opacity-50 text-sm"
            >
              Concluir dia
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
